import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { enquiriesApi } from "@/lib/enquiries";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface NewEnquiryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// NewEnquiryDialog
// ---------------------------------------------------------------------------

/**
 * Modal for creating a new enquiry from the EnquiriesListPage "+ New Enquiry"
 * action. Mirrors NewQuoteDialog (Plan II Task 7) — client picker + description
 * calling `enquiriesApi.create`. On success, navigates to `/enquiries/:id`.
 * The `/enquiries/new` route remains wired to NewEnquiryPage as a deep-link
 * fallback.
 *
 * Contract:
 * - State (client, description, error, mutation) is reset every time `open`
 *   transitions to `true`. Caller can mount once and toggle `open`.
 * - Errors surface inline in the dialog (no toast). Caller does not need to
 *   handle rejection — the dialog stays open on failure and shows the message.
 */
export function NewEnquiryDialog({ open, onOpenChange }: NewEnquiryDialogProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [client, setClient] = useState<ClientPickerValue | null>(null);
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      enquiriesApi.create({
        clientId: client?.clientId ?? null,
        description: description.trim() || null,
      }),
    onSuccess: (e) => {
      void qc.invalidateQueries({ queryKey: ["enquiries"] });
      onOpenChange(false);
      navigate(`/enquiries/${e.enquiryId}`);
    },
    onError: (err: Error) =>
      setError(err.message || "Failed to create enquiry"),
  });

  // Reset all draft state whenever the dialog transitions to open. Mirrors
  // NewQuoteDialog so Cancel/Esc/overlay-close doesn't leak prior input into
  // the next open.
  useEffect(() => {
    if (open) {
      setClient(null);
      setDescription("");
      setError(null);
      create.reset();
    }
    // `create` intentionally excluded to avoid an infinite reset loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!description.trim()) {
      setError("Description is required");
      return;
    }
    create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-label="New enquiry">
        <DialogHeader>
          <DialogTitle>New enquiry</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ned-client">Client</Label>
            <ClientPicker value={client} onChange={setClient} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ned-description">Description</Label>
            <Textarea
              id="ned-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enquiry description"
              className="min-h-[120px] resize-none"
              maxLength={500}
            />
            <div className="text-right text-xs text-muted-foreground mt-1">
              {description.length} / 500
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create Enquiry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
